import * as Flags from "./lib/Flags";
import * as LocaleUtils from "./lib/util/LocaleUtils";
import ApiDef from "./lib/api/ApiDef";
import * as EnvironmentUtils from "./lib/util/EnvironmentUtils";
import * as StorageUtils from "./lib/util/StorageUtils";
import * as NavigationUtils from "./lib/util/NavigationUtils";

import {
	CacheSource,
	RequestEvent,
	RequestMethod,
	ResponseType,
} from "./lib/api/ApiTypes";
import CacheMiddleware from "./lib/api/middleware/CacheMiddleware";
import TokenMiddleware from "./lib/TokenMiddleware";
import LoggingMiddleware from "./lib/api/middleware/LoggingMiddleware";
import { Platform, PushMessageTypeId, ScopeInfo } from "./lib/types/MiscTypes";
import { Invite, User, UserTimelineEvent } from "./lib/types/UserTypes";
import { Credential } from "./lib/util/AuthUtils";
import { Fleet, FleetStats, FleetUserCounts } from "./lib/types/Fleet";
import { Insight } from "./lib/types/InsightTypes";
// import { Dashboard } from "./lib/types/DashboardTypes";
import {
	InviteDriversResult,
	InviteDriverValidation,
} from "./components/form/InviteDriversForm/InviteDriversForm.reducer";
import { PillarBoxplotsData } from "./components/custom/PillarBoxplot/PillarBoxplot";
import { Group } from "./lib/types/Group";
import {
	EMULATED_FLEET_ID_KEY,
	EMULATED_GROUP_ID_KEY,
	EMULATED_USER_AUTH_LEVEL_KEY,
	EMULATED_USER_LEVEL_KEY,
} from "./StorageKeys";
import { FileResponse, FileSecureURLResponse } from "lib/types/FileTypes";
import {
	Journey,
	JourneyAggregate,
	JourneyPoi,
} from "./lib/types/JourneyTypes";
import { Report, ReportResponse } from "./lib/types/Reports";
import { Settings } from "./lib/types/Settings";
import { TimeUnit } from "./lib/consts/DatetimeConstants";
import { TrophyFeed } from "./lib/types/TrophyTypes";
import store from "./store";
import * as UserSelectors from "./store/User/User.selector";
import {
	CheckActionCodeResponse,
	MultiFactorInfo,
} from "./lib/types/AuthTypes";
import { FilterElement, SortElement } from "./lib/types/FilterSortTypes";
import { isErrorCode } from "./lib/util/ErrorUtils";

export interface DataPage<T> {
	results: T[];
	token?: string;
	nextToken?: string;
	pageCount?: number;
	totalResultCount?: number;
	pageIndex: number;
}

const getState: any = () => store.getState();

export const API_DEF = new ApiDef({
	name: "Brightmile V1",
	baseUrl: EnvironmentUtils.getBaseUrl(),
	middleware: [
		{
			[RequestEvent.Error]: (context) => {
				if (
					isErrorCode(
						context.error,
						"auth/multi-factor-mandated-require-enroll",
					)
				) {
					NavigationUtils.go("/multi-factor-mandate");
					return {
						type: "cancel",
					} as any;
				}
			},
			[RequestEvent.BeforeSend]: (context) => {
				context.updateHeaders({
					Locale:
						LocaleUtils.getState().code || LocaleUtils.DEFAULT_LOCALE.code,
				});
			},
			[RequestEvent.Success]: (context) => {
				const cacheStatus =
					context.response?.headers["brightmile-cache-status"];
				if (!context.cacheInfo.cached && cacheStatus === "read") {
					context.cacheInfo.cached = true;
					context.cacheInfo.source = CacheSource.Api;
				}
			},
		},
		CacheMiddleware({
			predicate: () => Flags.REQUEST_CACHE.is(),
		}),
		LoggingMiddleware({
			predicate: () => Flags.NETWORK_LOGGING.is(),
		}),
		TokenMiddleware(),
	],
	defaults: () => ({
		options: {
			cache: true,
		},
		headers: {
			"brightmile-version": EnvironmentUtils.getAppInfo().version,
			Platform: "portal",
			ApiKey: EnvironmentUtils.getApiKey(),
			"brightmile-emulated-user-level":
				StorageUtils.get(EMULATED_USER_AUTH_LEVEL_KEY) ||
				(StorageUtils.get(EMULATED_USER_LEVEL_KEY) as any),
			"brightmile-emulated-fleet-id": StorageUtils.get(
				EMULATED_FLEET_ID_KEY,
				UserSelectors.selectCurrentScopeFleetId(getState()),
			) as any,
			"brightmile-emulated-group-id": StorageUtils.get(
				EMULATED_GROUP_ID_KEY,
				UserSelectors.selectCurrentScopeGroupId(getState()),
			) as any,
		},
	}),
	mocking: {
		loader: () => import("./lib/mock/ApiMocks"),
		predicate: () => EnvironmentUtils.isTest() || Flags.DEMO_MODE.is(),
	},
});

export default API_DEF;

// -----------------
//    Endpoints
// -----------------

export const auth = API_DEF.endpoint<
	Credential,
	undefined,
	undefined,
	{ username: string; password: string }
>({
	id: "auth",
	name: "Sign in",
	description: "Provide a username and password to login",

	path: "/auth",
	method: RequestMethod.Post,
	defaults: {},
});

export const fetchParamTree = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	undefined
>({
	id: "fetch_param_tree",
	name: "Fetch Param Tree",
	description: "Fetches the param tree",

	method: RequestMethod.Get,
	path: "/status/view/param-tree",
});

export const fetchFleetJourneys = API_DEF.endpoint<
	any,
	"fleetId",
	{
		from?: number;
		to?: number;
		group?: string;
		userId?: string;
		includeManual?: boolean;
	},
	undefined
>({
	id: "fetch_fleet_journeys",
	name: "Fleet Journeys",
	description: "Retrieves a list of journeys for a time span",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/journeys",
});

export const fetchFleetJourneysPage = API_DEF.endpoint<
	DataPage<Journey>,
	"fleetId",
	{
		userId?: string;
		group?: string;
		pageIndex: number;
		pageSize: number;
		pageToken?: string;
	},
	undefined
>({
	id: "fetch_fleet_journeys_page",
	name: "Fleet Journeys Page",
	description: "Retrieves a page of journeys",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/journeys/page",
});

export const fetchFleetLogs = API_DEF.endpoint<
	any[],
	"fleetId",
	{
		from?: number;
		to?: number;
	},
	undefined
>({
	id: "fetch_fleet_logs",
	name: "Fleet Logs",
	description: "Retrieves a list of logs for a fleet",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/logs",
});

export const fetchFleetLogsPage = API_DEF.endpoint<
	DataPage<any>,
	"fleetId",
	{
		pageSize?: number;
		pageToken?: string;
	},
	undefined
>({
	id: "fetch_fleet_logs_page",
	name: "Fleet Logs Page",
	description: "Retrieves a log page for a fleet",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/logs/page",
});

export const fetchFleetVehicles = API_DEF.endpoint<
	any,
	"fleetId",
	{
		group?: string;
	},
	undefined
>({
	id: "fetch_fleet_vehicles",
	name: "Fleet Vehicles",
	description: "Retrieves a list of vehicles for a fleet",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/vehicles",
});

export const fetchFleetJourneyStats = API_DEF.endpoint<
	JourneyAggregate[],
	"fleetId",
	{
		timeRange?: TimeUnit;
		from?: number;
		to?: number;
		group?: string;
		type?: string;
	},
	undefined
>({
	id: "fetch_fleet_journey_stats",
	name: "Fleet Journey Stats",
	description:
		"Retrieves a list of aggregated journeys for a time range and span",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/stats/journeys",
});

export const fetchFleetReports = API_DEF.endpoint<
	ReportResponse,
	"fleetId",
	{
		timeRange?: TimeUnit;
		from?: number;
		to?: number;
		formId?: string;
		group?: string;
		groupIds?: string[];
		type?: string;
	},
	undefined
>({
	id: "fetch_fleet_reports",
	name: "Fleet Reports",
	description:
		"Retrieves a list of vehicle checks reports for a time range and span",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/reports/vehicle-checks",
});

export const fetchComplianceReports = API_DEF.endpoint<
	ReportResponse,
	"fleetId",
	{
		timeRange?: TimeUnit;
		from?: number;
		to?: number;
		formId?: string;
		userId?: string;
		group?: string | null | undefined;
		groupIds?: string[];
		type?: string;
	},
	undefined
>({
	id: "fetch_compliance_reports",
	name: "Compliance Reports",
	description: "Retrieves a list of compliance reports",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/reports/compliance",
});

export const fetchComplianceReport = API_DEF.endpoint<
	ReportResponse,
	"fleetId" | "formId",
	{
		timeRange?: TimeUnit;
		from?: number;
		to?: number;
		formId?: string;
		userId?: string;
		group?: string | null | undefined;
		groupIds?: string[];
		type?: string;
	},
	undefined
>({
	id: "fetch_compliance_report",
	name: "Compliance Report",
	description: "Retrieves a compliance report",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/reports/compliance-report/:formId",
});

export const fetchGroupRankingReport = API_DEF.endpoint<
	ReportResponse,
	"fleetId",
	{
		timeRange?: TimeUnit;
		from?: number;
		to?: number;
		period?: string;
		groupBy?: string;
		group?: string;
		groupIds?: string[];
		type?: string;
	},
	undefined
>({
	id: "fetch_group_ranking_report",
	name: "Group Ranking Report",
	description:
		"Retrieves a list of group ranking reports for a time range and span",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/reports/group-ranking",

	defaults: {
		options: {
			maxRetries: 0,
		},
	},
});

export const fetchUserRankingReport = API_DEF.endpoint<
	ReportResponse,
	"fleetId",
	{
		timeRange?: TimeUnit;
		from?: number;
		to?: number;
		period?: string;
		groupBy?: string;
		group?: string;
		groupIds?: string[];
		type?: string;
	},
	undefined
>({
	id: "fetch_user_ranking_report",
	name: "User Ranking Report",
	description:
		"Retrieves a list of user ranking reports for a time range and span",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/reports/user-ranking",

	defaults: {
		options: {
			maxRetries: 0,
		},
	},
});

export const fetchBrightCoachGroupRankingReport = API_DEF.endpoint<
	ReportResponse,
	"fleetId",
	{
		timeRange?: TimeUnit;
		from?: number;
		to?: number;
		period?: string;
		groupBy?: string;
		group?: string;
		groupIds?: string[];
		type?: string;
	},
	undefined
>({
	id: "fetch_brightcoach-group_ranking_report",
	name: "BrightCoach Group Ranking Report",
	description:
		"Retrieves a list of brightcoach group ranking reports for a time range and span",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/reports/brightcoach-group-ranking",
});

export const fetchBrightCoachUserRankingReport = API_DEF.endpoint<
	ReportResponse,
	"fleetId",
	{
		timeRange?: TimeUnit;
		from?: number;
		to?: number;
		period?: string;
		groupBy?: string;
		group?: string;
		groupIds?: string[];
		type?: string;
	},
	undefined
>({
	id: "fetch_brightcoach_user_ranking_report",
	name: "BrightCoach User Ranking Report",
	description:
		"Retrieves a list of brightcoach user ranking reports for a time range and span",

	method: RequestMethod.Get,
	path: "/fleet/:fleetId/reports/brightcoach-user-ranking",
});

export const authCredentials = API_DEF.endpoint<
	Credential,
	undefined,
	undefined,
	{ credential: string }
>({
	id: "auth_credentials",
	name: "Credential Sign In",
	description: "Signs into the portal with elevated perms",

	method: RequestMethod.Post,
	path: "auth/credential-auth",
});

export const fetchCurrentUser = API_DEF.endpoint<
	User,
	undefined,
	{ detailed?: boolean; scopes?: string[] },
	undefined
>({
	id: "current_user_data",
	name: "Current User Data",
	description: "Gets information about current user",

	method: RequestMethod.Get,
	path: "user",
	defaults: {
		options: {
			lock: "current_user_details",
		},
	},
});

export const fetchUser = API_DEF.endpoint<
	User,
	"userId",
	{ detailed?: boolean; scopes?: string[] },
	undefined
>({
	id: "user_data",
	name: "User Data",
	description: "Gets information about a single user",

	method: RequestMethod.Get,
	path: "user/:userId",
	defaults: {
		options: {
			lock: "user_details",
		},
	},
});

export const fetchUserFromToken = API_DEF.endpoint<
	User,
	undefined,
	undefined,
	undefined
>({
	id: "user_data_token",
	name: "User Data from Token",
	description: "Gets information about a single user from a token",
	method: RequestMethod.Get,
	path: "user",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const postChangePassword = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{
		username: string;
		password: string;
		newPassword: string;
	}
>({
	id: "auth_change_password",
	name: "Change Password",
	description: "Change user password",

	method: RequestMethod.Post,
	path: "auth/change-password",
});

export const postCheckActionCode = API_DEF.endpoint<
	CheckActionCodeResponse,
	undefined,
	undefined,
	{
		type: string;
		actionCode: string;
	}
>({
	id: "auth_check_action_code",
	name: "Check Action Code",
	description: "Check an action code",

	method: RequestMethod.Post,
	path: "auth/check-action-code",
});

export const postApplyResetPassword = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{
		actionCode: string;
		password: string;
	}
>({
	id: "apply_reset_password",
	name: "Apply Reset Password",
	description: "Apply reset password",

	method: RequestMethod.Post,
	path: "auth/apply-reset-password",
});

export const postVerifyManager = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{
		email: string;
	}
>({
	id: "auth_verify_manager",
	name: "Verify Manager",
	description: "Verify manager",

	method: RequestMethod.Post,
	path: "auth/verify-manager",
});

export const postToggleAccount = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	any
>({
	id: "auth_toggle_account",
	name: "Toggle Account",
	description: "Toggle account",

	method: RequestMethod.Post,
	path: "auth/toggle-account",
});

export const postVerifyEmail = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ email: string }
>({
	id: "post_verify_email",
	name: "Verify Email",
	description: "Verify email",

	method: RequestMethod.Post,
	path: "auth/verify-email",
});

export const postApplyVerifyEmail = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ actionCode: string }
>({
	id: "auth_apply_verify_email",
	name: "Apply Verify Email",
	description: "Applies the verification email code",

	method: RequestMethod.Post,
	path: "auth/apply-verify-email",
});

export const postSendResetPassword = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{
		email: string;
		platform?: string;
	}
>({
	id: "auth_send_reset_password",
	name: "Send Reset Password",
	description: "Send reset password email",

	method: RequestMethod.Post,
	path: "auth/send-reset-password",
});

export const postGenerateActionLink = API_DEF.endpoint<
	string,
	undefined,
	undefined,
	{
		email: string;
		type: string;
	}
>({
	id: "auth_generate_action_link",
	name: "Generate Action Link",
	description: "Generate Action Link",

	method: RequestMethod.Post,
	path: "auth/generate-action-link",

	responseType: ResponseType.Text,
});

export interface SendAppPushResult {
	sent: number;
	platforms: Platform[];
}

export const postUserSendAppPush = API_DEF.endpoint<
	SendAppPushResult,
	"userId",
	undefined,
	{
		type: PushMessageTypeId;
		title?: string;
		body?: string;
	}
>({
	id: "user_send_app_push",
	name: "Send App Push",
	description: "Send an app push notification",

	method: RequestMethod.Post,
	path: "user/:userId/send-app-push",
});

export const postUserUpdate = API_DEF.endpoint<User, "userId", undefined, any>({
	id: "user_update",
	name: "User Update",
	description: "Updates user information",

	method: RequestMethod.Post,
	path: "user/:userId",
});

export const postUserDeleteFederated = API_DEF.endpoint<
	void,
	"userId",
	undefined,
	undefined
>({
	id: "user_delete_federated",
	name: "User Delete Federated",
	description: "Deletes a federated user on environments other than production",

	method: RequestMethod.Post,
	path: "user/:userId/delete-federated",
});

export const postJourneyUpdate = API_DEF.endpoint<
	any,
	"journeyId",
	undefined,
	{
		transportType?: string;
		mode?: string;
		type?: string;
		hidden?: boolean;
		unscorable?: boolean;
	}
>({
	id: "journey_update",
	name: "Journey Update",
	description: "Updates a journey",

	method: RequestMethod.Post,
	path: "journey/:journeyId",
});

export const postManagerSignUp = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{
		actionCode: string;
		firstName: string;
		lastName: string;
		company: string;
		primaryWorkLocation: string;
		country: string;
		phoneNumber?: string;
		jobTitle?: string;
		other?: string;
		password: string;
		confirmPassword: string;
	}
>({
	id: "auth_manager_sign_up",
	name: "Manager Sign Up",
	description: "Sign up a manager only account",

	method: RequestMethod.Post,
	path: "auth/manager-sign-up",
});

export const postInviteManager = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{
		email: string;
		fleetId: string;
		group: string;
		programId: string;
		admin: boolean;
	}
>({
	id: "auth_invite_manager",
	name: "Invite Manager",
	description: "Invite a manager only user",

	method: RequestMethod.Post,
	path: "auth/invite-manager",
});

export const postUserCreate = API_DEF.endpoint<
	User,
	undefined,
	undefined,
	{
		email: string;
		firstName: string;
		lastName: string;
		country: string;
		fleetId: string;
		password: string;
		confirmPassword: string;
		verifyEmail?: boolean;
		debug?: boolean;
		userLevel?: string;
		group?: string;
	}
>({
	id: "auth_user_create",
	name: "Create User",
	description: "Force create a user account",

	method: RequestMethod.Post,
	path: "user",
});

export const fetchInsights = API_DEF.endpoint<
	Insight[],
	undefined,
	{
		fleetId?: string;
		group?: string;
		includeTemp?: boolean;
		from?: number;
		to?: number;
	},
	undefined
>({
	id: "insights",
	name: "Insights",
	description: "Gets all insight statuses for a fleet or group",

	method: RequestMethod.Get,
	path: "insights",
	defaults: {
		options: {
			lock: "insights",
		},
	},
});

export const postInsightCreate = API_DEF.endpoint<
	Insight,
	undefined,
	undefined,
	any
>({
	id: "insight_create",
	name: "Insight Create",
	description: "Create an insight",

	method: RequestMethod.Post,
	path: "insights",
});

export const postInsightUpdate = API_DEF.endpoint<
	Insight,
	undefined,
	undefined,
	{ id: string; insightData: any }
>({
	id: "insight_update",
	name: "Insight Update",
	description: "Updates an insight",

	method: RequestMethod.Post,
	path: "insights/update",
});

export const postInsightUserStatusUpdate = API_DEF.endpoint<
	any,
	"userId" | "insightId",
	undefined,
	any
>({
	id: "insight_update",
	name: "Insight Update",
	description: "Updates user insight information",

	method: RequestMethod.Post,
	path: "insights/:userId/insight/:insightId",
});

export const postInsightUpdateBulk = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ insights: any[] }
>({
	id: "insight_update_bulk",
	name: "Bulk Insight Update",
	description: "Bulk update insights",

	method: RequestMethod.Post,
	path: "insights/update-bulk",
});

export const postInsightUpdateSurvey = API_DEF.endpoint<
	any,
	"userId" | "insightId",
	undefined,
	any
>({
	id: "insight_update_survey",
	name: "Insight Survey Update",
	description: "Updates survey insight information",

	method: RequestMethod.Post,
	path: "insights/:userId/insight/:insightId/survey",
});

export const fetchUserInsights = API_DEF.endpoint<
	any[],
	"userId",
	Partial<ScopeInfo>,
	undefined
>({
	id: "user_insights",
	name: "User Insights",
	description: "Gets all insight statuses for a user",

	method: RequestMethod.Get,
	path: "insights/:userId",
	defaults: {
		options: {
			lock: "user_insights",
		},
	},
});

export const fetchUserTrophies = API_DEF.endpoint<
	TrophyFeed,
	"userId",
	{ portal?: boolean },
	undefined
>({
	id: "user_trophies",
	name: "User Trophies",
	description: "Gets all trophies for user",

	method: RequestMethod.Get,
	path: "user/:userId/trophies",
});

export const postUserMigrateEmail = API_DEF.endpoint<
	User,
	"userId",
	undefined,
	{ newEmail: string }
>({
	id: "user_migrate_email",
	name: "Migrate Email",
	description: "Migrate user's email",

	method: RequestMethod.Post,
	path: "user/:userId/migrate-email",
});

export const postUserRefreshStatus = API_DEF.endpoint<
	any[],
	"userId",
	undefined,
	undefined
>({
	id: "user_refresh_status",
	name: "Refresh User Status",
	description: "Get the latest status for a user",

	method: RequestMethod.Post,
	path: "user/:userId/refresh-status",
});

export interface UserGiveBrightmilesResponse {
	user: User;
	sentMessages: number;
}

export const postUserGiveBrightCoins = API_DEF.endpoint<
	UserGiveBrightmilesResponse,
	"userId",
	undefined,
	{
		amount: number;
		notify?: {
			title: string;
			body: string;
		};
	}
>({
	id: "user_give_brightcoins",
	name: "Give BrightCoins",
	description: "Give user BrightCoins",

	method: RequestMethod.Post,
	path: "user/:userId/give-brightcoins",
});

export const fetchFleetStatsPoi = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string; from?: number; type?: "risk" | "eco" | "distraction" },
	undefined
>({
	id: "fleet_poi_stats",
	name: "Fleet POI Stats",
	description: "Gets aggregate POIs for time range in fleet",

	method: RequestMethod.Get,
	path: "fleet/:fleetId/stats/pois",

	defaults: {
		options: {
			cache: 60 * 1000 * 60,
			lock: (context) =>
				`fleet_aggregate_pois_${context.computedConfig.query.type}`,
		},
	},
});

export const fetchJourney = API_DEF.endpoint<
	Journey,
	"journeyId",
	{},
	undefined
>({
	id: "journey",
	name: "Journey Info",
	description: "Gets journey info",

	method: RequestMethod.Get,
	path: "journey/:journeyId",
});

export const fetchJourneyPois = API_DEF.endpoint<
	JourneyPoi[],
	"journeyId",
	{ version?: number },
	undefined
>({
	id: "journey_pois",
	name: "Journey POIs",
	description: "Gets journey POIs",

	method: RequestMethod.Get,
	path: "journey/:journeyId/poi",
});

export const fetchFleetStats = API_DEF.endpoint<
	FleetStats,
	"fleetId",
	{ group?: string },
	undefined
>({
	id: "fleet_stats",
	name: "Fleet Stats",
	description: "Gets stats summary",

	method: RequestMethod.Get,
	path: "fleet/:fleetId/stats",
});

export const fetchFleetUsers = API_DEF.endpoint<
	User[],
	"fleetId",
	{
		includeInvites?: boolean;
		group?: string | null;
		detailed?: boolean;
		includeManagers?: boolean;
		scopes?: string[];
		includeArchived?: boolean;
		filter?: FilterElement;
		sort?: SortElement[];
		limit?: number;
		archiveRetentionWindow?: number;
	},
	undefined
>({
	id: "fleet_users",
	name: "Fleet Users",
	description: "Gets all users in a fleet",

	method: RequestMethod.Get,
	path: "fleet/:fleetId/users",

	defaults: {
		options: {
			lock: "fleet_users",
			cache: false,
		},
	},
});

export const fetchFleetUsersEmails = API_DEF.endpoint<
	{ emails: string[] },
	"fleetId",
	Omit<UsersQuery, "detailed">,
	undefined
>({
	id: "fleet_users_emails",
	name: "Fleet Users Emails",
	description: "Gets all users emails in the fleet fleet",

	method: RequestMethod.Get,
	path: "fleet/:fleetId/users/emails",

	defaults: {
		options: {
			lock: "fleet_users_emails",
			cache: false,
		},
	},
});

export type UsersQuery = {
	includeInvites?: boolean;
	group?: string;
	detailed?: boolean;
	scopes?: string[];
	filter?: FilterElement;
	sort?: SortElement[];
	pageIndex?: number;
	pageSize: number;
};

export const fetchFleetUsersPage = API_DEF.endpoint<
	DataPage<User | Invite>,
	"fleetId",
	UsersQuery,
	undefined
>({
	id: "fleet_users_page",
	name: "Fleet Users Page",
	description: "Gets all users in a fleet by pages",

	method: RequestMethod.Get,
	path: "fleet/:fleetId/users/page",

	defaults: {
		options: {
			lock: "fleet_users",
			cache: false,
		},
	},
});

export const postFleetUsersExport = API_DEF.endpoint<
	string,
	"fleetId",
	{ group?: string },
	{ filter: FilterElement; sort: SortElement[] }
>({
	id: "fleet_users_export",
	name: "Fleet Users Export",
	description: "Gets all users in a fleet as a CSV",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/export",
	responseType: ResponseType.Text,
});

export const fetchFleets = API_DEF.endpoint<
	Fleet[],
	undefined,
	{ detailed?: boolean; includeUserSummary?: boolean; showArchived?: boolean },
	undefined
>({
	id: "fleets",
	name: "Fleets",
	description: "Gets all fleets",

	method: RequestMethod.Get,
	path: "fleet",
});

export const fetchFiles = API_DEF.endpoint<
	FileResponse,
	"fleetId",
	{ groupHierarchy?: string; groupId?: string },
	undefined
>({
	id: "files",
	name: "Files",
	description: "Gets all files",
	method: RequestMethod.Get,
	path: "fleet/:fleetId/files",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const fetchFileSecureUrl = API_DEF.endpoint<
	FileSecureURLResponse,
	"fleetId",
	{ fileId: string; groupId?: string; friendlyName?: string },
	undefined
>({
	id: "fileSecureURL",
	name: "File Secure URL",
	description: "Gets a secure URL for a file",
	method: RequestMethod.Get,
	path: "fleet/:fleetId/files/getSecureUrl",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const postUploadFiles = API_DEF.endpoint<
	any,
	"fleetId",
	{
		groupHierarchy: string;
		groupId?: string;
		friendlyName?: string;
		generateRootFolder?: boolean;
	},
	FormData
>({
	id: "uploadFiles",
	name: "Upload File",
	description: "Upload a file to to storage",
	method: RequestMethod.Post,
	path: "fleet/:fleetId/files/upload",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const postRenameFile = API_DEF.endpoint<
	any,
	"fleetId" | "fileId",
	{ newFileName: string; groupId?: string },
	undefined
>({
	id: "renameFile",
	name: "Rename File",
	description: "Rename a file in storage",
	method: RequestMethod.Post,
	path: "fleet/:fleetId/files/:fileId/rename",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const postMoveFile = API_DEF.endpoint<
	any,
	"fleetId",
	{ oldFilePath: string; targetFolderPath: string },
	undefined
>({
	id: "moveFile",
	name: "Move File",
	description: "Move a file in storage",
	method: RequestMethod.Post,
	path: "fleet/:fleetId/files/move",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const postDeleteFile = API_DEF.endpoint<
	any,
	"fleetId" | "fileId",
	{ groupId?: string },
	undefined
>({
	id: "deleteFile",
	name: "Delete File",
	description: "Delete a file from storage",
	method: RequestMethod.Post,
	path: "fleet/:fleetId/files/:fileId/delete",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const postFleetGroupUpdate = API_DEF.endpoint<
	Group,
	"fleetId" | "groupId",
	undefined,
	any
>({
	id: "fleet_group_update",
	name: "Fleet Group Update",
	description: "Update a fleet's group info",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/group/:groupId",
});

export const postFleetGroupArchive = API_DEF.endpoint<
	Group,
	"fleetId" | "groupId",
	undefined,
	any
>({
	id: "fleet_group_archive",
	name: "Fleet Group Archive",
	description: "Marks group for archive",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/group/:groupId/archive",
});

export const postFleetCreate = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{
		id: string;
		name: string;
		status?: string;
		details?: {
			registeredAddress?: string;
			logoUrl?: string;
			website?: string;
			insurer?: string;
		};
	}
>({
	id: "fleet_create",
	name: "Create Fleet",
	description: "Create a fleet",

	method: RequestMethod.Post,
	path: "fleet",
});

export const postGroupCreate = API_DEF.endpoint<
	Group,
	"fleetId",
	undefined,
	{
		id: string;
		name: string;
		ostrichMode?: boolean;
	}
>({
	id: "group_create",
	name: "Create Group",
	description: "Create a group for a fleet",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/group",
});

export const fetchFleet = API_DEF.endpoint<
	Fleet,
	"fleetId",
	{ group?: string | null } | undefined,
	undefined
>({
	id: "fetch_fleet",
	name: "Fetch Fleet",
	description: "Get fleet information",

	method: RequestMethod.Get,
	path: "fleet/:fleetId",

	defaults: {
		options: {
			lock: "fleet",
		},
	},
});

export const postFleetUpdate = API_DEF.endpoint<
	Fleet,
	"fleetId",
	{ includeGroups?: boolean },
	Partial<Omit<Fleet, "settings"> & { settings?: Settings }>
>({
	id: "fleet_update",
	name: "Fleet Update",
	description: "Fleet update",

	method: RequestMethod.Post,
	path: "fleet/:fleetId",
});

export const postFleetArchive = API_DEF.endpoint<
	Fleet,
	"fleetId",
	undefined,
	undefined
>({
	id: "fleet_archive",
	name: "Fleet Archive",
	description: "Fleet archive",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/archive",
});

export const postFleetUnarchive = API_DEF.endpoint<
	Fleet,
	"fleetId",
	undefined,
	undefined
>({
	id: "fleet_unarchive",
	name: "Fleet Unarchive",
	description: "Fleet unarchive",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/unarchive",
});

export const fetchFleetViewPillarBoxplots = API_DEF.endpoint<
	PillarBoxplotsData,
	"fleetId",
	{ group?: string; ostrichMode?: boolean; appType: string; vendor: string }
>({
	id: "fleet_view_pillar_boxplots",
	name: "Fleet View Pillar Boxplots",
	description: "Used for the dashboard box plots",

	method: RequestMethod.Get,
	path: "fleet/:fleetId/view/dashboard-pillar-boxplots",

	defaults: {
		options: {
			lock: "fleet_view_pillar_boxplots",
		},
	},
});

export const fetchFleetUserCounts = API_DEF.endpoint<
	FleetUserCounts,
	"fleetId",
	{
		group?: string;
	},
	undefined
>({
	id: "fleet_users_count",
	name: "Fleet User Count",
	description: "Gets the user count scores",

	method: RequestMethod.Get,
	path: "fleet/:fleetId/users/count",

	defaults: {
		options: {
			lock: "fleet_user_counts",
		},
	},
});

/**
 * @deprecated
 */
export const fetchFleetUsersScores = API_DEF.endpoint<
	any,
	"fleetId",
	{
		ostrichMode?: boolean;
		from?: number;
		to?: number;
		timeRange?: TimeUnit;
		group?: string;
	},
	undefined
>({
	id: "fleet_users_scores",
	name: "Fleet Users Scores",
	description: "Gets the scores for users in a fleet",

	method: RequestMethod.Get,
	path: "fleet/:fleetId/stats/users/scores",

	defaults: {
		options: {
			lock: "fleetUsersScores",
		},
	},
});

export const postInviteDrivers = API_DEF.endpoint<
	InviteDriversResult,
	"fleetId",
	{ group?: string },
	any
>({
	id: "invite_drivers",
	name: "Invite Drivers",
	description: "Invites drivers to a fleet",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/invite",
});

export const postSendReminder = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string },
	{
		email: string;
		subject?: string;
		message?: string;
	}
>({
	id: "reminder_user",
	name: "Reminder User",
	description: "Send a reminder email to user",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/remind",
});

export const postSendReminderBulk = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string },
	{
		subject?: string;
		message?: string;
		filter: FilterElement;
		excludeRecent: boolean;
	}
>({
	id: "reminder_user_bulk",
	name: "Remind Users Bulk",
	description: "Send bulk reminders",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/bulk-remind",

	defaults: {
		options: {
			maxRetries: 0,
		},
	},
});

export const postSendInactiveMessage = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string | null },
	{
		subject?: string;
		message?: string;
		email: string;
	}
>({
	id: "send_inactive_message",
	name: "Send User Inactive Message",
	description: "Send inactive email to a single user",
	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/message-inactive",
});

export const postSendBulkInactiveMessage = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string },
	{
		subject?: string;
		message?: string;
		filter: FilterElement;
		excludeRecent: boolean;
	}
>({
	id: "send_bulk_inactive_message",
	name: "Send Users Inactive Message",
	description: "Send inactive email to multiple users",
	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/bulk-message-inactive",
});

export const postSetDriverContacted = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string | null },
	{
		email: string;
		status: string;
	}
>({
	id: "set_driver_contacted",
	name: "Set driver to contacted",
	description: "Updates latest message sent and message count for message type",
	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/set-contacted",
});

export const postInviteEdit = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string },
	{ inviteId: string; data: any }
>({
	id: "invite_edit",
	name: "Invite Edit",
	description: "Edit an invite",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/invite/edit",
});

export const postInviteCancel = API_DEF.endpoint<
	any,
	"fleetId",
	{
		group?: string;
	},
	{ inviteId: string }
>({
	id: "invite_cancel",
	name: "Invite Cancel",
	description: "Cancel an invite",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/invite/cancel",
});

export const postInviteRetry = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string },
	{ email: string }
>({
	id: "invite_retry",
	name: "Invite Retry",
	description: "Retry an invite",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/invite/retry",
});

export const postValidateInvites = API_DEF.endpoint<
	InviteDriverValidation[],
	"fleetId",
	{ group?: string },
	{
		invites: string[];
	}
>({
	id: "validate_invites",
	name: "Validate Invites",
	description: "Validates driver invites",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/validate-invites",
});

export const postValidateJml = API_DEF.endpoint<
	InviteDriverValidation[],
	"fleetId",
	{ group?: string },
	{
		invites: string[];
	}
>({
	id: "validate_jml",
	name: "Validate Jml",
	description: "Validates driver jml",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/validate-jml",
});

export const postArchiveJml = API_DEF.endpoint<
	InviteDriverValidation[],
	"fleetId",
	{ group?: string },
	{
		invites: string[];
	}
>({
	id: "archive_jml",
	name: "Archive Jml",
	description: "Archives driver jml",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/archive-jml",
});

export const postDeleteInvitesJml = API_DEF.endpoint<
	InviteDriverValidation[],
	"fleetId",
	{ group?: string },
	{
		invites: string[];
	}
>({
	id: "delete_invites_jml",
	name: "Delete Invites Jml",
	description: "Deletes Invites jml",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/delete-invites-jml",
});

export const postGroupChangeJml = API_DEF.endpoint<
	InviteDriverValidation[],
	"fleetId",
	{ group?: string },
	{
		invites: string[];
	}
>({
	id: "group_change_jml",
	name: "Group Change Jml",
	description: "Group Change jml",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/users/group-change-jml",
});

export const fetchUserJourneys = API_DEF.endpoint<
	any,
	"userId",
	{ timeRange?: TimeUnit; from?: number; to?: number; type?: string },
	undefined
>({
	id: "user_journeys",
	name: "User Journeys",
	description: "Get user journeys",

	method: RequestMethod.Get,
	path: "user/:userId/journeys-list",
});

export const fetchUserDetectionAvoidanceSummary = API_DEF.endpoint<
	any,
	"userId",
	undefined,
	undefined
>({
	id: "user_user-detection-avoidance-summary",
	name: "User Detection Avoidance",
	description: "Get a summary of a user's detection avoidance",

	method: RequestMethod.Get,
	path: "user/:userId/view/user-detection-avoidance-summary",

	defaults: {
		options: {
			lock: "user_detection_avoidance_summary",
		},
	},
});

export const fetchUserStats = API_DEF.endpoint<
	any,
	"userId",
	undefined,
	undefined
>({
	id: "user_stats",
	name: "User Stats",
	description: "Get user stats",

	method: RequestMethod.Get,
	path: "user/:userId/stats",

	defaults: {
		options: {
			lock: "user_stats",
		},
	},
});

export const fetchDetectionAvoidance = API_DEF.endpoint<
	any,
	"userId",
	undefined,
	undefined
>({
	id: "user_detection_avoidance",
	name: "Detection Avoidance",
	description: "Get detection avoidance usage stats",

	method: RequestMethod.Get,
	path: "user/:userId/view/user-detection-avoidance-summary",

	defaults: {
		options: {
			lock: "user_detection_avoidance",
		},
	},
});

export const fetchUserGuide = API_DEF.endpoint<
	{ userGuideUrl: string },
	undefined,
	undefined,
	undefined
>({
	id: "user_guide",
	name: "User Guide",
	description: "Get the user guide",

	method: RequestMethod.Get,
	path: "misc/user-guide",
});

export const postPdfConverter = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ html: string; header?: string; footer?: string }
>({
	id: "pdf_converter",
	name: "PDF Converter",
	description: "Convert html to a PDF",

	responseType: ResponseType.ArrayBuffer,
	method: RequestMethod.Post,
	path: "misc/export/pdf",
});

export const postImageConverter = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ html: string; header?: string; footer?: string }
>({
	id: "image_converter",
	name: "Image Converter",
	description: "Convert html to a PNG",

	responseType: ResponseType.ArrayBuffer,
	method: RequestMethod.Post,
	path: "misc/export/image",
});

export const postUserPurgeAndRescore = API_DEF.endpoint<
	true,
	"userId",
	undefined,
	undefined
>({
	id: "purge_and_rescore",
	name: "Purge and Rescore",
	description: "Purge cache and re-score user",

	method: RequestMethod.Post,
	path: "user/:userId/purge-and-rescore",
});

export const fetchMileageExportDownload = API_DEF.endpoint<
	any,
	"userId",
	{ from?: number; to?: number },
	undefined
>({
	id: "fetch_mileage_export",
	name: "Mileage Export",
	description: "Download a CSV of the user's mileage export",

	responseType: ResponseType.ArrayBuffer,
	method: RequestMethod.Get,
	path: "user/:userId/mileage/report",
});

export const fetchCompetitions = API_DEF.endpoint<
	{ comps: any[]; prizes: any[] },
	undefined,
	{
		group?: string;
		fleetId?: string;
		includeGlobal?: boolean;
	},
	undefined
>({
	id: "fetch_competitions",
	name: "Competitions",
	description: "Get a list of competitions",

	method: RequestMethod.Get,
	path: "rewards/competitions",
});

export const fetchCompetitionEntries = API_DEF.endpoint<
	any[],
	"competitionId",
	undefined,
	undefined
>({
	id: "fetch_competitions_entries",
	name: "Competition Entries",
	description: "Get a list of competition entries",

	method: RequestMethod.Get,
	path: "rewards/competitions/competition/:competitionId/entries",
});

export const postSendCompetitionAwards = API_DEF.endpoint<
	any,
	"competitionId",
	undefined,
	{
		sendResultEmail: boolean;
		prizeValue: string;
		voucherCode?: string;
		sendWinnerEmail?: boolean;
	}
>({
	id: "post_send_competition_awards",
	name: "Send Competition Awards",
	description: "Triggers send of rewards winners campaign",
	method: RequestMethod.Post,
	path: "rewards/competitions/competition/:competitionId/send-awards",
});

export const postCreateCompetition = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ [key: string]: any }
>({
	id: "post_create_competition",
	name: "Create Competition",
	description: "Creates a competition or competitions",
	method: RequestMethod.Post,
	path: "rewards/competitions",
});

export const postCreatePilotCompetitions = API_DEF.endpoint<
	any,
	undefined,
	{ fleetId: string; group?: string },
	{ competitions: any; duration: string }
>({
	id: "post_create_pilot_competition",
	name: "Create Pilot Competitions",
	description: "Creates a pilot competition or competitions",
	method: RequestMethod.Post,
	path: "rewards/pilot-competitions",
});

export const postCancelPilotCompetitions = API_DEF.endpoint<
	any,
	undefined,
	{ fleetId: string; group?: string },
	{ competitions: any }
>({
	id: "post_cancel_pilot_competition",
	name: "Cancel Pilot Competitions",
	description: "Cancels a pilot competition or competitions",
	method: RequestMethod.Post,
	path: "rewards/cancel-pilot-competitions",
});

export const postEditCompetition = API_DEF.endpoint<
	any,
	"compId",
	undefined,
	{ [key: string]: any }
>({
	id: "post_create_competition",
	name: "Create Competition",
	description: "Creates a competition",
	method: RequestMethod.Post,
	path: "rewards/competitions/competition/:compId",
});

export const postDeleteCompetition = API_DEF.endpoint<
	any,
	"compId",
	undefined,
	{ [key: string]: any }
>({
	id: "post_delete_competition",
	name: "Delete Competition",
	description: "Deletes a competition",
	method: RequestMethod.Post,
	path: "rewards/competitions/competition/:compId/delete",
});

export const postUpdateCompetitions = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ competitions: any[] }
>({
	id: "post_updated_competitions",
	name: "Update Competitions",
	description: "Updates several competitions",
	method: RequestMethod.Post,
	path: "rewards/competitions/update-competitions",
});

export const fetchPrograms = API_DEF.endpoint<
	any[],
	undefined,
	undefined,
	undefined
>({
	id: "fetch_programs",
	name: "Programs",
	description: "Get a list of programs",

	method: RequestMethod.Get,
	path: "program",
});

export const fetchProgram = API_DEF.endpoint<
	any[],
	"programId",
	undefined,
	undefined
>({
	id: "fetch_program",
	name: "Program",
	description: "Get a program",

	method: RequestMethod.Get,
	path: "program/:programId",
});

export const postFleetPlanCreate = API_DEF.endpoint<
	any,
	"fleetId" | "programId",
	undefined,
	any
>({
	id: "fleet_plan_create",
	name: "Fleet Plan Create",
	description: "Create a plan for the fleet with the program",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/program/:programId/plan",
});

export const postFleetPlanToggle = API_DEF.endpoint<
	any,
	"fleetId" | "programId",
	undefined,
	{ enabled: boolean }
>({
	id: "fleet_plan_toggle",
	name: "Fleet Plan Toggle",
	description: "Toggles a fleet's plan for the program",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/program/:programId/toggle",
});

export interface GroupInfo {
	id: string;
	groups: GroupInfo[];
}

export const postFleetRestructure = API_DEF.endpoint<
	any,
	"fleetId",
	undefined,
	{ groups: GroupInfo[] }
>({
	id: "fleet_restructure",
	name: "Fleet Restructure",
	description: "Move groups around inside a fleet",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/restructure",
});

export const postGroupDelete = API_DEF.endpoint<
	any,
	"fleetId" | "groupId",
	undefined,
	undefined
>({
	id: "fleet_group_delete",
	name: "Group Delete",
	description: "Delete a group that is empty",

	method: RequestMethod.Post,
	path: "fleet/:fleetId/group/:groupId/delete",
});

export const fetchPortalNotificationLevel = API_DEF.endpoint<any>({
	id: "fetch_portal_notification_level",
	name: "Portal Notification Level",
	description: "Get portal notification level and message",
	method: RequestMethod.Get,
	path: "misc/portal-notification",
});

export interface MultiFactorChangeResponse {
	auth?: Credential;
	multiFactorInfo: MultiFactorInfo;
}

export const postMultiFactorEnrollFactor = API_DEF.endpoint<
	MultiFactorChangeResponse,
	undefined,
	undefined,
	{
		email?: string;
		factorId: "phone";
		sessionInfo: string;
		code: string;
		password: string;
	}
>({
	id: "post_multi_factor_enroll",
	name: "Multi Factor Enroll",
	description: "Enroll a user in multi-factor auth",
	method: RequestMethod.Post,
	path: "auth/multi-factor/enroll-factor",
});

export const postMultiFactorEnrollSendVerify = API_DEF.endpoint<
	{ sessionInfo: string },
	undefined,
	undefined,
	{
		factorId: "phone";
		factorData: string;
		recaptchaToken: string;
	}
>({
	id: "post_multi_factor_enroll_send_verify",
	name: "Multi Factor Enroll Send Verify",
	description: "Verify phone number for multi factor enrollment",
	method: RequestMethod.Post,
	path: "auth/multi-factor/enroll-send-verify",
});

export const postMultiFactorAuthSendVerify = API_DEF.endpoint<
	{ sessionInfo: string },
	undefined,
	undefined,
	{
		factorUid: string;
		recaptchaToken: string;
		pendingCredential: string;
	}
>({
	id: "post_multi_factor_auth_send_verify",
	name: "Multi Factor Auth Send Verify",
	description: "Verify phone number for multi factor auth",
	method: RequestMethod.Post,
	path: "auth/multi-factor/auth-send-verify",
});

export const postMultiFactorUnenrollFactor = API_DEF.endpoint<
	MultiFactorChangeResponse,
	undefined,
	undefined,
	{
		factorUid: string;
		password?: string;
		email?: string;
	}
>({
	id: "post_multi_factor_unenroll_factor",
	name: "Multi Factor Unenroll Factor",
	description: "Remove a factor",
	method: RequestMethod.Post,
	path: "auth/multi-factor/unenroll-factor",
});

export const postMultiFactorAuth = API_DEF.endpoint<
	Credential,
	undefined,
	undefined,
	{
		sessionInfo: string;
		pendingCredential: string;
		code: string;
	}
>({
	id: "post_multi_factor_auth",
	name: "Multi Factor Auth",
	description: "Auth with multi factor code",
	method: RequestMethod.Post,
	path: "auth/multi-factor/auth",
});

export const fetchSupportTimeline = API_DEF.endpoint<
	{ events: UserTimelineEvent[] },
	"userId"
>({
	id: "fetch_support_timeline",
	name: "Support Timeline",
	description: "Get user support timeline",
	method: RequestMethod.Get,
	path: "user/:userId/support-timeline",
});

export const postFleetInvalidateCache = API_DEF.endpoint<
	{ success: true },
	"fleetId",
	undefined,
	{ group?: string }
>({
	id: "fleet_invalidate_cache",
	name: "Invalidate Group & LTC Cache",
	description: "Invalidate group API & LTC cache ",
	method: RequestMethod.Post,
	path: "fleet/:fleetId/invalidate-cache",
});

export const fetchIdentifyChatUser = API_DEF.endpoint<any>({
	id: "identify_chat_user",
	name: "Identify Live Chat (Hubspot) User",
	description: "Identify Live Chat (Hubspot) User",
	method: RequestMethod.Get,
	path: "misc/chat-identify",
});

export const postSignOut = API_DEF.endpoint<any>({
	id: "post_sign_out",
	name: "Sign Out",
	description: "Remove token cookies",
	method: RequestMethod.Post,
	path: "auth/sign-out",
});

export interface CheckAccountResult {
	authType: string;
	providerId?: string;
}

export const fetchCheckAccount = API_DEF.endpoint<
	CheckAccountResult,
	undefined,
	{ email: string }
>({
	id: "fetch_check_account",
	name: "Check Account",
	description: "Checks auth type",
	method: RequestMethod.Get,
	path: "auth/check-account",
});

export interface SSOInfo {
	id: string;
	sso: boolean;
}
interface EmailSSOResult {
	fleet: SSOInfo;
	groups?: SSOInfo[];
}
export interface CheckGroupSSOResult {
	[email: string]: EmailSSOResult;
}

export const fetchCheckGroupSSO = API_DEF.endpoint<
	CheckGroupSSOResult,
	undefined,
	{ emails: string[]; groupIds: string[] }
>({
	id: "fetch_check_group_sso",
	name: "Check Group SSO",
	description: "Checks Group SSO for given groups and emails",
	method: RequestMethod.Get,
	path: "auth/check-group-sso",
});

export const postFederatedValidateAuth = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ userId: string; token: string; providerId: string; email: string }
>({
	id: "post_federated_validate_auth",
	description: "Post federated auth validate",
	method: RequestMethod.Post,
	name: "Validated Federated Auth",
	path: "auth/federated/validate-auth",
});

export const postPreviewManagerDigest = API_DEF.endpoint<
	{ success: true },
	"userId",
	undefined,
	{ manager: boolean }
>({
	id: "post_preview_manager_digest",
	name: "Preview Manager Digest",
	description: "Previews Manager Digest",
	method: RequestMethod.Post,
	path: "user/:userId/send-digest",
});

export const postCheckManagerFleetEmail = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ emailDomain: string }
>({
	id: "post_check_manager_fleet_email",
	name: "Check manager fleet email",
	description: "Checks manager email against fleet id",
	method: RequestMethod.Post,
	path: "misc/check-manager-fleet",
});

export const postCreateUserFavourite = API_DEF.endpoint<
	any,
	"userId",
	undefined,
	{ type: string; data: any; id: string }
>({
	id: "create_user_favourite",
	name: "Create user favourite",
	description: "Creates user favourite",
	method: RequestMethod.Post,
	path: "user/:userId/favourite",
});

export const postUpdateUserFavourite = API_DEF.endpoint<
	any,
	"userId" | "itemId",
	undefined,
	{ data: any }
>({
	id: "update_user_favourite",
	name: "Update user favourite",
	description: "Updates user favourite",
	method: RequestMethod.Post,
	path: "user/:userId/favourite/:itemId/update",
});

export const postDeleteUserFavourite = API_DEF.endpoint<
	any,
	"userId" | "itemId",
	undefined,
	undefined
>({
	id: "delete_user_favourites",
	name: "Delete user favourites",
	description: "Deletes user favourites",
	method: RequestMethod.Post,
	path: "user/:userId/favourite/:itemId/delete",
});

export const fetchUserFavourites = API_DEF.endpoint<
	any,
	"userId",
	{ type?: string },
	undefined
>({
	id: "fetch_user_favourites",
	name: "Fetch user favourites",
	description: "Fetches user favourites",
	method: RequestMethod.Get,
	path: "user/:userId/favourites",
	defaults: {
		options: {
			cache: false,
		},
	},
});

interface ResetOtpResponse {
	userId: string;
	email: string;
	password: string;
	otp: boolean;
}

export const postResetOtp = API_DEF.endpoint<
	ResetOtpResponse,
	undefined,
	undefined,
	{ userId: string; otpOff?: boolean; password?: string }
>({
	id: "reset_otp",
	name: "Reset One Time Password For User",
	description: "Resets OTP for user",
	method: RequestMethod.Post,
	path: "auth/change-password-otp",
});

export const fetchFleetGroupScores = API_DEF.endpoint<
	any,
	"fleetId",
	{
		groupBy?: string;
		group?: string;
		from: number;
		to: number;
		appType?: string;
		vendor?: string;
		bmOnlyUsers?: boolean;
	},
	undefined
>({
	id: "fetch_fleet_group_scores",
	name: "Fetch fleet group scores",
	description: "Fetches fleet or group scores",
	method: RequestMethod.Get,
	path: "/fleet/:fleetId/view/portal-scores-feed",
});

export const fetchUserScores = API_DEF.endpoint<
	any,
	"userId",
	{
		groupBy?: string;
		from: number;
		to: number;
		appType?: string;
		vendor?: string;
	},
	undefined
>({
	id: "fetch_user_scores",
	name: "Fetch user scores",
	description: "Fetches user scores",
	method: RequestMethod.Get,
	path: "/user/:userId/view/portal-scores-feed",
});

export const fetchFleetCompetitions = API_DEF.endpoint<
	any,
	"fleetId",
	{
		group?: string;
		includeDisabled?: boolean;
		includeGlobal?: boolean;
		includeFuture?: boolean;
		includeCompleted?: boolean;
		detailed?: boolean;
	},
	undefined
>({
	id: "fetch_fleet_competitions",
	name: "Fetch fleet competitions",
	description: "Fetches fleet competitions",
	method: RequestMethod.Get,
	path: "/rewards/competitions/fleet/:fleetId",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const fetchCompetitionResultData = API_DEF.endpoint<
	any,
	"fleetId" | "competitionId",
	{ group?: string },
	undefined
>({
	id: "fetch_competition_result_data",
	name: "Fetch competition result data",
	description: "Fetches competition result data",
	method: RequestMethod.Get,
	path: "/rewards/competitions/fleet/:fleetId/competition/:competitionId",
	defaults: {
		options: {
			cache: false,
		},
	},
});

export const fetchRewardsImages = API_DEF.endpoint<
	any,
	undefined,
	{ path?: string },
	undefined
>({
	id: "fetch_rewards_images",
	name: "Fetch rewards images",
	description: "Fetches all images for rewards",
	method: RequestMethod.Get,
	path: "/rewards/images",
});

export const postRewardsImageUpload = API_DEF.endpoint<
	any,
	undefined,
	{ path?: string },
	{ fileData: File }
>({
	id: "post_rewards_image",
	name: "Post rewards image",
	description: "Upload rewards image to cloud storage",
	method: RequestMethod.Post,
	path: "/rewards/upload-image",
});

export const postDeleteRewardsImage = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ path: string }
>({
	id: "post_delete_rewards_image",
	name: "Post delete rewards image",
	description: "Delete rewards image from cloud storage",
	method: RequestMethod.Post,
	path: "/rewards/delete-image",
});

export const postGetEditableRewardsImage = API_DEF.endpoint<
	any,
	undefined,
	undefined,
	{ imagePath: string }
>({
	id: "post_get_editable_rewards_image",
	name: "Post get editable rewards image",
	description: "Get editable rewards image as base64 string",
	method: RequestMethod.Post,
	path: "/rewards/get-editable-image",
});

export const postValidatePrizeRecipients = API_DEF.endpoint<
	{
		email: string;
		exists: boolean;
		fleetId?: string;
		group?: string;
		userId?: string;
	}[],
	undefined,
	undefined,
	{ emails: string[] }
>({
	id: "validate_prize_recipients",
	name: "Validate prize recipients",
	description: "Validates users exist before awarding them prizes",
	method: RequestMethod.Post,
	path: "/rewards/validate-prize-recipients",
});

export const postCreatePrize = API_DEF.endpoint<
	any[],
	undefined,
	undefined,
	{
		name: string;
		image: string;
		type: string;
		value: number;
		currency: string;
		userIds: { key: string }[];
	}
>({
	id: "create_prize",
	name: "Create prize",
	description: "Create rewards prize",
	method: RequestMethod.Post,
	path: "/rewards/create-prize",
});

export const fetchGroupLeaderboards = API_DEF.endpoint<
	any,
	"fleetId",
	{ group?: string; from?: number; to?: number; anonymise?: boolean },
	undefined
>({
	id: "fetch_group_leaderboards",
	name: "Fetch group leaderboards",
	description: "Fetch group leaderboards",
	method: RequestMethod.Get,
	path: "/fleet/:fleetId/leaderboards",
});

export const fetchLeaderboardRankings = API_DEF.endpoint<
	any,
	"fleetId" | "leaderboardId",
	{ limit: number; anonymise?: boolean; group?: string },
	undefined
>({
	id: "fetch_leaderboard_rankings",
	name: "Fetch leaderboard rankings",
	description: "Fetch leaderboard rankings",
	method: RequestMethod.Get,
	path: "/fleet/:fleetId/leaderboards/:leaderboardId",
});

window.__api = API_DEF;
